import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Calendar as CalendarIcon, Users, List, Search, X } from 'lucide-react';
import { useTasks, Task } from '../hooks/useTasks';
import { useOrders } from '../hooks/useOrders';
import { useClients } from '../hooks/useClients';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { format, isToday, isFuture } from 'date-fns';
import TaskCard from './workboard/TaskCard';
import TaskDrawer from './workboard/TaskDrawer';
import AddTaskModal from './workboard/AddTaskModal';
import TaskStats from './workboard/TaskStats';
import ClientGroup from './workboard/ClientGroup';

interface ModernWorkboardProps {}

type FilterTab = 'all' | 'today' | 'upcoming' | 'completed';
type ViewMode = 'list' | 'client';

const ModernWorkboard: React.FC<ModernWorkboardProps> = () => {
  const { user } = useAuth();
  const { tasks, loading, createTask, updateTask, deleteTask, refetchTasks } = useTasks();
  const { orders } = useOrders();
  const { clients } = useClients();
  const [searchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'todo' | 'in_progress' | 'completed' | null>(null);
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Get date from calendar if present
  const calendarDate = searchParams.get('date');
  const action = searchParams.get('action');

  // Ouvrir automatiquement le modal si action=create est présent
  React.useEffect(() => {
    if (action === 'create') {
      setIsAddModalOpen(true);
      // Nettoyer l'URL pour éviter de rouvrir le modal à chaque re-render
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('action');
      window.history.replaceState({}, '', `${window.location.pathname}?${newSearchParams.toString()}`);
    }
  }, [action, searchParams]);

  // Filter tasks based on active tab, calendar date, and selected client
  const filteredTasks = useMemo(() => {
    let filtered = [...tasks];

    // Apply client filter if selected
    if (selectedClientId) {
      const selectedClient = clients.find(c => c.id === selectedClientId);
      filtered = filtered.filter(task => {
        // Check if task has an order with the selected client
        if (task.order_id) {
          const order = orders.find(o => o.id === task.order_id);
          if (order?.client?.id === selectedClientId) return true;
        }
        // Also check task.order.client.name if available
        if (task.order?.client?.name && selectedClient?.name) {
          return task.order.client.name === selectedClient.name;
        }
        return false;
      });
    }

    // Apply calendar date filter if present
    if (calendarDate) {
      filtered = filtered.filter(task => {
        if (!task.due_date) return false;
        // Éviter les problèmes de timezone lors de la comparaison
        let taskDate: string;
        if (task.due_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          taskDate = task.due_date; // Déjà au bon format
        } else {
          const date = new Date(task.due_date);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          taskDate = `${year}-${month}-${day}`;
        }
        return taskDate === calendarDate;
      });
    }

    // Apply status filter if set
    if (statusFilter) {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    // Apply tab filter
    switch (activeTab) {
      case 'today':
        return filtered.filter(task => {
          if (!task.due_date) return false;
          // Éviter les problèmes de timezone
        if (task.due_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = task.due_date.split('-').map(Number);
          return isToday(new Date(year, month - 1, day));
        }
        return isToday(new Date(task.due_date));
        });
      case 'upcoming':
        return filtered.filter(task => {
          if (!task.due_date) return false;
          // Éviter les problèmes de timezone
          let dueDate: Date;
          if (task.due_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = task.due_date.split('-').map(Number);
            dueDate = new Date(year, month - 1, day);
          } else {
            dueDate = new Date(task.due_date);
          }
          return isFuture(dueDate) && !isToday(dueDate);
        });
      case 'completed':
        return filtered.filter(task => task.status === 'completed');
      default:
        // Apply overdue filter if active
        if (showOverdueOnly) {
          return filtered.filter(task => {
            if (!task.due_date || task.status === 'completed') return false;
            let taskDate: Date;
            if (task.due_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
              const [year, month, day] = task.due_date.split('-').map(Number);
              taskDate = new Date(year, month - 1, day);
            } else {
              taskDate = new Date(task.due_date);
            }
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            taskDate.setHours(0, 0, 0, 0);
            return taskDate < today;
          });
        }
        return filtered;
    }
  }, [tasks, activeTab, calendarDate, selectedClientId, orders, clients, statusFilter, showOverdueOnly]);

  const [clientSearchQuery, setClientSearchQuery] = useState('');

  // Group tasks by client and include all clients
  const tasksByClient = useMemo(() => {
    const grouped: Record<string, { tasks: Task[]; clientId?: string }> = {};
    
    // First, initialize with all clients from the clients list
    clients.forEach(client => {
      grouped[client.name] = { tasks: [], clientId: client.id };
    });
    
    // Then, add tasks to their respective clients
    filteredTasks.forEach(task => {
      let clientName: string | undefined;
      let clientId: string | undefined;
      
      // Try to get client from task.order.client
      if (task.order?.client?.name) {
        clientName = task.order.client.name;
        const order = orders.find(o => o.id === task.order_id);
        clientId = order?.client?.id;
      } else if (task.order_id) {
        // Try to get client from order
        const order = orders.find(o => o.id === task.order_id);
        if (order?.client?.name) {
          clientName = order.client.name;
          clientId = order.client.id;
        }
      }
      
      // If we found a client name, add the task
      if (clientName) {
        if (!grouped[clientName]) {
          grouped[clientName] = { tasks: [], clientId };
        }
        grouped[clientName].tasks.push(task);
      } else {
        // Tasks without a client go to "No Client" group
        if (!grouped['No Client']) {
          grouped['No Client'] = { tasks: [], clientId: undefined };
        }
        grouped['No Client'].tasks.push(task);
      }
    });

    // Filter by search query
    const filtered = Object.entries(grouped).filter(([clientName]) => {
      if (!clientSearchQuery) return true;
      return clientName.toLowerCase().includes(clientSearchQuery.toLowerCase());
    });

    // Sort by client name (but keep "No Client" at the end if it exists)
    const sorted = filtered.sort(([a], [b]) => {
      if (a === 'No Client') return 1;
      if (b === 'No Client') return -1;
      return a.localeCompare(b);
    });

    return sorted.reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {} as Record<string, { tasks: Task[]; clientId?: string }>);
  }, [filteredTasks, clients, orders, clientSearchQuery]);

  const toggleClientExpansion = (clientName: string) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientName)) {
        next.delete(clientName);
      } else {
        next.add(clientName);
      }
      return next;
    });
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setIsDrawerOpen(true);
  };

  const handleCreateTask = async (taskData: {
    title: string;
    description: string;
    priority: 'low' | 'medium' | 'high';
    due_date: string;
    order_id?: string;
    client_id?: string;
  }) => {
    // If client_id is provided but no order_id, find or create order association
    let finalOrderId = taskData.order_id;
    
    if (taskData.client_id && !taskData.order_id && orders.length > 0) {
      // Try to find an order for this client
      const clientOrder = orders.find(o => o.client?.id === taskData.client_id);
      if (clientOrder) {
        finalOrderId = clientOrder.id;
      }
    }

    const success = await createTask({
      ...taskData,
      status: 'todo',
      estimated_hours: 1,
      due_date: taskData.due_date || undefined,
      order_id: finalOrderId || undefined,
    });

    if (success) {
      await refetchTasks();
    }
  };

  const handleUpdateTask = async (taskId: string, updates: Partial<Task>) => {
    const success = await updateTask(taskId, updates);
    if (success) {
      await refetchTasks();
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const success = await deleteTask(taskId);
    if (success) {
      await refetchTasks();
      setIsDrawerOpen(false);
      setSelectedTask(null);
    }
  };

  const tabs: { id: FilterTab; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'today', label: 'Today' },
    { id: 'upcoming', label: 'Upcoming' },
    { id: 'completed', label: 'Completed' },
  ];

  if (loading) {
    return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9c68f2]" />
        </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full overflow-hidden">
      {/* Calendar Date Info */}
      {calendarDate && (
        <div className="text-sm text-[rgb(var(--muted))] flex items-center gap-2">
          <CalendarIcon className="w-4 h-4" />
          Showing tasks for {(() => {
            // Éviter les problèmes de timezone
            if (calendarDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
              const [year, month, day] = calendarDate.split('-').map(Number);
              return format(new Date(year, month - 1, day), 'MMMM dd, yyyy');
            }
            return format(new Date(calendarDate), 'MMMM dd, yyyy');
          })()}
        </div>
      )}

      {/* Controls Bar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between w-full">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full">
          {/* View Mode Toggle */}
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-2 theme-card-bg theme-card-border rounded-lg p-1 w-full sm:w-auto">
            <button
              onClick={() => setViewMode('list')}
              className={`w-full sm:w-auto px-4 py-2 rounded-md transition-colors flex items-center justify-center gap-2 border ${
                viewMode === 'list' 
                  ? 'bg-gray-900 text-white border-gray-900 hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 dark:border-white' 
                  : 'bg-transparent text-[rgb(var(--muted))] border-transparent hover:text-[rgb(var(--fg))] dark:hover:text-white'
              }`}
            >
              <List className="w-4 h-4" />
              List
            </button>
            <button
              onClick={() => setViewMode('client')}
              className={`w-full sm:w-auto px-4 py-2 rounded-md transition-colors flex items-center justify-center gap-2 border ${
                viewMode === 'client'
                  ? 'bg-gray-900 text-white border-gray-900 hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 dark:border-white' 
                  : 'bg-transparent text-[rgb(var(--muted))] border-transparent hover:text-[rgb(var(--fg))] dark:hover:text-white'
              }`}
            >
              <Users className="w-4 h-4" />
              By Client
            </button>
          </div>
          
          {/* Client Filter */}
          {viewMode === 'list' && clients.length > 0 && (
            <select
              value={selectedClientId || ''}
              onChange={(e) => setSelectedClientId(e.target.value || null)}
              className="w-full sm:w-48 px-4 py-2.5 theme-input theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          )}
        </div>

        <button
          onClick={() => setIsAddModalOpen(true)}
          className="w-full sm:w-auto px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm font-medium border border-gray-900 bg-gray-900 text-white hover:bg-black dark:border-white dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add Task
        </button>
      </div>

      {/* Statistics Section */}
      <TaskStats 
        tasks={tasks} 
        activeFilter={
          showOverdueOnly ? 'overdue' :
          activeTab === 'completed' ? 'completed' :
          activeTab === 'today' ? 'today' :
          statusFilter || null
        }
        onStatClick={(filter) => {
          if (filter === null) {
            // Total Tasks - réinitialiser tous les filtres
            setActiveTab('all');
            setStatusFilter(null);
            setShowOverdueOnly(false);
          } else if (filter === 'completed') {
            setActiveTab('completed');
            setStatusFilter(null);
            setShowOverdueOnly(false);
          } else if (filter === 'today') {
            setActiveTab('today');
            setStatusFilter(null);
            setShowOverdueOnly(false);
          } else if (filter === 'in_progress' || filter === 'todo') {
            setActiveTab('all');
            setStatusFilter(filter);
            setShowOverdueOnly(false);
          } else if (filter === 'overdue') {
            setActiveTab('all');
            setStatusFilter(null);
            setShowOverdueOnly(true);
          }
        }}
      />
            
      {/* Tab Switcher */}
      <div className="flex items-center gap-2 border-b theme-card-border overflow-x-auto scrollbar-hide">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
                return (
                        <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                // Reset filters when switching tabs
                setStatusFilter(null);
                setShowOverdueOnly(false);
              }}
              className={`flex-shrink-0 px-4 sm:px-6 py-3 font-medium transition-colors relative ${
                isActive
                  ? 'text-[rgb(var(--brand))] dark:text-[rgb(var(--brand))]'
                  : 'text-[rgb(var(--muted))] hover:text-[rgb(var(--muted))]'
              }`}
            >
              {tab.label}
              {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"
                  initial={false}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
                        </button>
                );
              })}
            </div>

      {/* Tasks Content - List or Client View */}
      <AnimatePresence mode="wait">
        {filteredTasks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center py-16"
          >
            <div className="w-16 h-16 theme-card-bg rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarIcon className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-lg font-semibold theme-text-primary mb-2">No tasks found</h3>
            <p className="text-[rgb(var(--muted))] mb-6">
              {activeTab === 'all' && !calendarDate && !selectedClientId
                ? 'Get started by creating your first task'
                : `No tasks match your current filter`}
            </p>
            {activeTab === 'all' && !calendarDate && !selectedClientId && (
                <button
                onClick={() => setIsAddModalOpen(true)}
                className="px-6 py-2.5 bg-blue-600 text-[rgb(var(--fg))] rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Create Task
                </button>
            )}
          </motion.div>
        ) : viewMode === 'client' ? (
          // Client Group View
          <motion.div
            key={`client-${activeTab}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {/* Client Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[rgb(var(--muted))]" />
                  <input
                    type="text"
                value={clientSearchQuery}
                onChange={(e) => setClientSearchQuery(e.target.value)}
                placeholder="Search clients..."
                className="w-full pl-10 pr-10 py-3 theme-input theme-text-primary rounded-lg placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              {clientSearchQuery && (
                <button
                  onClick={() => setClientSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:theme-hover-button rounded transition-colors"
                >
                  <X className="w-4 h-4 text-[rgb(var(--muted))]" />
                </button>
                )}
                </div>
                
            {/* Client Groups */}
            {Object.entries(tasksByClient).length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[rgb(var(--fg))] mb-2">No clients found</h3>
                <p className="text-[rgb(var(--muted))]">
                  {clientSearchQuery 
                    ? 'Try a different search term' 
                    : 'No clients available. Create a client first.'}
                </p>
                  </div>
                ) : (
              Object.entries(tasksByClient).map(([clientName, clientData]) => (
                <ClientGroup
                  key={clientName}
                  clientName={clientName}
                  tasks={clientData.tasks}
                  isExpanded={expandedClients.has(clientName)}
                  onToggle={() => toggleClientExpansion(clientName)}
                  onTaskClick={handleTaskClick}
                    />
                  ))
                )}
          </motion.div>
        ) : (
          // List View
          <motion.div
            key={`list-${activeTab}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {filteredTasks.map((task) => (
                    <TaskCard 
                      key={task.id} 
                      task={task} 
                onClick={() => handleTaskClick(task)}
                        />
                      ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Task Drawer */}
      <TaskDrawer
        task={selectedTask}
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedTask(null);
        }}
        onUpdate={handleUpdateTask}
        onDelete={handleDeleteTask}
        orders={orders.map(order => ({ id: order.id, title: order.title }))}
      />

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onCreate={handleCreateTask}
        orders={orders.map(order => ({ 
          id: order.id, 
          title: order.title,
          client: order.client
        }))}
        clients={clients.map(client => ({ id: client.id, name: client.name }))}
        defaultDate={calendarDate || undefined}
                  />
                  </div>
  );
};

export default ModernWorkboard;
